import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const FAQSection = styled.section`
  padding: 80px 0;
  background-color: #121E42;
  color: white;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  color: white;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled(motion.div)`
  margin-bottom: 20px;
  border-radius: 10px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const FAQQuestion = styled.div`
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background-color: rgba(0, 229, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(0, 229, 255, 0.15);
  }
  
  h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #00E5FF;
  }
`;

const FAQAnswer = styled(motion.div)`
  padding: 0;
  height: 0;
  overflow: hidden;
  
  div {
    padding: 20px;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.6;
  }
`;

const ToggleIcon = styled.span<{ isOpen: boolean }>`
  width: 24px;
  height: 24px;
  position: relative;
  
  &:before, &:after {
    content: '';
    position: absolute;
    background-color: #00E5FF;
    transition: all 0.3s ease;
  }
  
  &:before {
    width: 100%;
    height: 2px;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
  }
  
  &:after {
    width: 2px;
    height: 100%;
    top: 0;
    left: 50%;
    transform: translateX(-50%) ${({ isOpen }) => isOpen ? 'scaleY(0)' : 'scaleY(1)'};
  }
`;

interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  
  const faqItems: FAQItemProps[] = [
    {
      question: "Qual a duração das pós-graduações do Grupo Nobre?",
      answer: "As pós-graduações do Grupo Nobre têm duração de 6 a 15 meses, dependendo do curso escolhido. A carga horária varia de 360 a 420 horas, atendendo às exigências do MEC para cursos de especialização."
    },
    {
      question: "Como funciona o modelo EAD das pós-graduações?",
      answer: "Nosso modelo EAD oferece flexibilidade total de horários, com aulas gravadas de alta qualidade, material didático digital, tutoria especializada e encontros síncronos opcionais. A plataforma está disponível 24h por dia, 7 dias por semana, permitindo que você estude no seu ritmo."
    },
    {
      question: "Preciso ter experiência prévia na área para fazer a pós-graduação?",
      answer: "Para a maioria dos cursos, é necessário apenas ter graduação completa na área da saúde. Alguns cursos específicos podem exigir formação em áreas determinadas, como Enfermagem ou Medicina. Recomendamos verificar os pré-requisitos específicos de cada curso."
    },
    {
      question: "Os certificados do Grupo Nobre são reconhecidos pelo MEC?",
      answer: "Sim, todos os nossos cursos de pós-graduação são reconhecidos pelo MEC e atendem às exigências da Resolução CNE/CES nº 1, de 6 de abril de 2018, que estabelece as normas para o funcionamento de cursos de pós-graduação lato sensu."
    },
    {
      question: "Como funciona o processo de matrícula?",
      answer: "O processo de matrícula é 100% online. Após preencher o formulário de interesse, nossos consultores entrarão em contato para orientar sobre documentação necessária, formas de pagamento e acesso à plataforma. Todo o processo pode ser concluído em poucos minutos."
    }
  ];
  
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  
  return (
    <FAQSection id="faq">
      <div className="container">
        <SectionTitle>Perguntas Frequentes</SectionTitle>
        <FAQContainer>
          {faqItems.map((item, index) => (
            <FAQItem 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <FAQQuestion onClick={() => toggleFAQ(index)}>
                <h3>{item.question}</h3>
                <ToggleIcon isOpen={openIndex === index} />
              </FAQQuestion>
              <FAQAnswer
                animate={{ 
                  height: openIndex === index ? 'auto' : 0,
                  opacity: openIndex === index ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
              >
                <div>{item.answer}</div>
              </FAQAnswer>
            </FAQItem>
          ))}
        </FAQContainer>
      </div>
    </FAQSection>
  );
};

export default FAQ;
